import { frameDisplayUrl } from "../utils/assetMatching";
import type { Scene } from "../types";
import { frameSourceClass, frameSourceLabel, frameStatus, formatChunking, formatRuntime, sceneStatus, sceneStoryboardIssue, sceneStoryboardReadyCount, storyboardFrameIsReady } from "../utils/storyboard";

interface StoryboardReviewListProps {
  scenes: Scene[];
  selectedSceneId: string;
  onSelectScene: (sceneId: string) => void;
}

export function StoryboardReviewList({ scenes, selectedSceneId, onSelectScene }: StoryboardReviewListProps) {
  const allFrames = scenes.flatMap((scene) => scene.frames);
  const readyScenes = scenes.filter((scene) => scene.readiness === "ready").length;
  const totalBoards = allFrames.length;
  const queuedBoards = allFrames.filter((frame) => frame.queuedRegenerate || frame.generationStatus === "queued").length;
  const generatingBoards = allFrames.filter((frame) => frame.generationStatus === "generating").length;
  const failedBoards = allFrames.filter((frame) => frame.generationStatus === "failed").length;
  const readyBoards = allFrames.filter(storyboardFrameIsReady).length;
  const uploadedBoards = allFrames.filter((frame) => frame.source === "uploaded").length;
  const lockedBoards = allFrames.filter((frame) => frame.locked).length;
  const disabledBoards = allFrames.filter((frame) => frame.disabled).length;

  return (
    <section className="storyboard-review-list" aria-label="Storyboard review">
      <header>
        <div>
          <h2>Storyboard Review</h2>
          <p>
            <strong>{readyScenes} / {scenes.length} scenes ready</strong>
            <span>{uploadedBoards} uploaded · {lockedBoards} locked · {disabledBoards} disabled · {queuedBoards} queued · {generatingBoards} generating · {failedBoards} failed</span>
          </p>
        </div>
        <span>{readyBoards} / {totalBoards} boards ready · Fix only what's flagged</span>
      </header>

      <div className="review-scene-list">
        {scenes.map((scene) => {
          const boardIssue = sceneStoryboardIssue(scene);
          const readySceneBoards = sceneStoryboardReadyCount(scene);
          return (
          <button className={scene.id === selectedSceneId ? "selected" : ""} key={scene.id} onClick={() => onSelectScene(scene.id)} type="button">
            <span className="review-scene-number">{scene.number}</span>
            <div className="review-scene-copy">
              <strong>{scene.slugline}</strong>
              <small>{scene.location} · {readySceneBoards}/{scene.frames.filter((frame) => !frame.disabled).length} boards ready</small>
              <time>{formatRuntime(scene.durationSeconds)} · {formatChunking(scene.durationSeconds)}</time>
            </div>
            <div className="review-thumbs">
              {scene.frames.slice(0, 4).map((frame) => (
                <span key={frame.id}>
                  <img src={frameDisplayUrl(frame)} alt="" />
                  <b className={`review-source ${frameSourceClass(frame)}`}>{frameSourceLabel(frame)}</b>
                  <em>{frameStatus(frame)}</em>
                </span>
              ))}
            </div>
            <div className="review-scene-state">
              <em className={`readiness ${scene.readiness}`}>{sceneStatus(scene)}</em>
              {boardIssue ? <em className="readiness needs_voice">{boardIssue}</em> : <em className="readiness ready">Boards ready</em>}
              <span>{"Open ->"}</span>
            </div>
          </button>
          );
        })}
      </div>
    </section>
  );
}
